import { overlay as overlayClassName } from './SankeyOverlay.sty.js';
import { useOverlay } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useSankeyOverlayEvents } from '../hooks/use-sankey-overlay-events.js';
import { useSankeyHoveredShape } from '../store/selectors.js';

export const SANKEY_OVERLAY_ARIA_LABEL = 'Sankey chart interaction overlay';

/**
 * Exposes the hovered shape as `data-hovered-*` attributes so the dev-app
 * demo can read it via a `MutationObserver` without importing the
 * package-internal hover store. Stripped in production builds (dead-code
 * eliminated once bundlers inline `NODE_ENV`) so real consumers never see an
 * implicit, undocumented DOM contract on the shipped component.
 */
function getHoverDebugAttributes(
  hovered: ReturnType<typeof useSankeyHoveredShape>,
) {
  if (process.env.NODE_ENV === 'production') {
    return undefined;
  }

  return {
    'data-hovered-type': hovered?.type,
    'data-hovered-id':
      hovered &&
      (hovered.type === 'node'
        ? hovered.node.id
        : `${hovered.source.id}->${hovered.target.id}`),
    'data-hovered-related-links':
      hovered?.type === 'node'
        ? String(hovered.relatedLinks.length)
        : undefined,
    'data-hovered-related':
      hovered?.type === 'node'
        ? String(hovered.relatedNodes.length)
        : undefined,
  };
}

export function SankeyOverlay() {
  const overlay = useOverlay();
  const eventHandlers = useSankeyOverlayEvents(overlay);
  const hovered = useSankeyHoveredShape();
  const hoverDebugAttributes = getHoverDebugAttributes(hovered);

  return (
    <div
      className={overlayClassName}
      ref={overlay.overlayRef}
      role="graphics-symbol"
      aria-label={SANKEY_OVERLAY_ARIA_LABEL}
      // TODO: Remove in APPDEV-19252
      {...hoverDebugAttributes}
      {...eventHandlers}
    />
  );
}

SankeyOverlay['displayName'] = 'SankeyOverlay';
